// CSV parsing and generation utilities

export function parseCSV(csvText: string): string[][] {
  const lines: string[][] = [];
  let currentLine: string[] = [];
  let currentField = '';
  let insideQuotes = false;

  for (let i = 0; i < csvText.length; i++) {
    const char = csvText[i];
    const nextChar = csvText[i + 1];

    if (char === '"') {
      if (insideQuotes && nextChar === '"') {
        // Escaped quote
        currentField += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        insideQuotes = !insideQuotes;
      }
    } else if (char === ',' && !insideQuotes) {
      // Field separator
      currentLine.push(currentField);
      currentField = '';
    } else if ((char === '\n' || char === '\r') && !insideQuotes) {
      // Line break
      if (currentField || currentLine.length > 0) {
        currentLine.push(currentField);
        lines.push(currentLine);
        currentLine = [];
        currentField = '';
      }
      // Skip \r\n combination
      if (char === '\r' && nextChar === '\n') {
        i++;
      }
    } else {
      currentField += char;
    }
  }

  // Add last field and line
  if (currentField || currentLine.length > 0) {
    currentLine.push(currentField);
    lines.push(currentLine);
  }

  return lines;
}

export function csvToObjects<T extends Record<string, any>>(
  csvText: string,
  columnMapping?: Record<string, string>
): T[] {
  const lines = parseCSV(csvText);
  if (lines.length === 0) return [];

  const headers = lines[0];
  const data: T[] = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (line.length === 0 || (line.length === 1 && line[0] === '')) continue;

    const obj: any = {};
    for (let j = 0; j < headers.length; j++) {
      const header = headers[j];
      const value = line[j] || '';
      const mappedKey = columnMapping?.[header] || header;
      obj[mappedKey] = value;
    }
    data.push(obj as T);
  }

  return data;
}

export function objectsToCSV<T extends Record<string, any>>(
  data: T[],
  headers?: string[]
): string {
  if (data.length === 0) return '';

  const keys = headers || Object.keys(data[0]);
  const lines: string[] = [];

  // Add header row
  lines.push(keys.map(escapeCSVField).join(','));

  // Add data rows
  for (const obj of data) {
    const row = keys.map((key) => {
      const value = obj[key];
      return escapeCSVField(value != null ? String(value) : '');
    });
    lines.push(row.join(','));
  }

  return lines.join('\n');
}

function escapeCSVField(field: string): string {
  if (field.includes(',') || field.includes('"') || field.includes('\n') || field.includes('\r')) {
    return `"${field.replace(/"/g, '""')}"`;
  }
  return field;
}

export function downloadCSV(filename: string, csvContent: string): void {
  // UTF-8 BOM so Google Ads Editor on Windows decodes accented characters correctly
  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
}
